import { AttachFile, CheckCircle, OpenInNew, RadioButtonUnchecked, Send } from "@mui/icons-material";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Link, MenuItem, Select, Table, TableBody,
	TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useMemo, useState } from "react";
import { modalNewDeliveryDocument } from "../../../atoms/atom";
import { FinancialService } from "../../../services/FinancialService";
import { DeliveryDocumentDTO } from "../../../types/DeliveryDocumentDTO";
import { FinancialBillDTO, FinancialDeliveryContextDTO, FinancialDocumentDTO, FinancialDocumentRowDTO } from "../../../types/FinancialDTO";
import { formatDate, monthLabel } from "../../../utils/financialFunctions";
import { matchesCheckedFilter } from "../../../utils/financialSelection";
import { useAlert } from "../../contextProviders/AlertProvider";
import CreateDeliveryModal from "../../documents/create-delivery-modal/CreateDeliveryModal";
import CreateTemplateModal from "../../documents/create-template-modal/CreateTemplateModal";
import FinancialUploadModal from "../upload-modal/FinancialUploadModal";
import "./FinancialDocuments.scss";

const EMPTY_DELIVERY = {} as DeliveryDocumentDTO;

const FinancialDocuments: FC = () => {
	const [months, setMonths] = useState<string[]>([]);
	const [month, setMonth] = useState('');
	const [rows, setRows] = useState<FinancialDocumentRowDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [checkedFilter, setCheckedFilter] = useState('ALL');
	const [ascending, setAscending] = useState(true);
	const [uploadBill, setUploadBill] = useState<FinancialBillDTO | null>(null);
	const [deliveryBill, setDeliveryBill] = useState<FinancialBillDTO | null>(null);
	const [context, setContext] = useState<FinancialDeliveryContextDTO | null>(null);
	const [preparing, setPreparing] = useState<number | null>(null);
	const [removing, setRemoving] = useState<FinancialDocumentDTO | null>(null);
	const [removeBusy, setRemoveBusy] = useState(false);
	const [openDelivery, setOpenDelivery] = useAtom(modalNewDeliveryDocument);
	const { showAlert, showError } = useAlert();
	const service = new FinancialService();

	useEffect(() => {
		const loadMonths = async () => {
			const response = await service.getMonths();
			const available = response?.data || [];
			setMonths(available);
			setMonth(available[0] || '');
			if (!available.length) {
				setLoading(false);
			}
		};
		loadMonths();
	}, []);

	const load = async () => {
		if (!month) {
			return;
		}
		setLoading(true);
		const response = await service.getDocuments(month);
		if (!response) {
			showError('Não foi possível carregar os documentos. Recarregue a página para tentar novamente.');
		}
		setRows(response?.data || []);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [month]);

	useEffect(() => {
		if (!openDelivery && deliveryBill) {
			setDeliveryBill(null);
			setContext(null);
			load();
		}
	}, [openDelivery]);

	const visible = useMemo(() => {
		const term = search.trim().toLowerCase();
		const digits = term.replace(/\D/g, '');
		return rows
			.filter(row => matchesCheckedFilter(row.checked, checkedFilter))
			.filter(row => !term || row.bill.companyName?.toLowerCase().includes(term)
				|| (!!digits && (row.bill.document || '').replace(/\D/g, '').includes(digits)))
			.sort((a, b) => (ascending ? 1 : -1) * (a.bill.companyName || '').localeCompare(b.bill.companyName || ''));
	}, [rows, search, checkedFilter, ascending]);

	const prepareDelivery = async (bill: FinancialBillDTO) => {
		setPreparing(bill.idConta);
		const response = await service.getDeliveryContext(bill.idConta);
		setPreparing(null);
		if (!response) {
			showError('Não foi possível preparar o envio. Tente novamente.');
			return;
		}
		setContext(response.data);
		setDeliveryBill(bill);
		setOpenDelivery(true);
	};

	const submitDelivery = async (form: FormData) => {
		if (!deliveryBill) {
			return false;
		}
		try {
			await service.deliver(deliveryBill.idConta, form);
			return true;
		} catch (error: any) {
			showError(typeof error?.response?.data === 'string' ? error.response.data : undefined);
			return false;
		}
	};

	const removeDocument = async () => {
		if (!removing) {
			return;
		}
		setRemoveBusy(true);
		try {
			await service.removeDocument(removing.id);
			showAlert('Arquivo removido');
			load();
		} catch (error: any) {
			showError(typeof error?.response?.data === 'string' ? error.response.data : undefined);
		}
		setRemoveBusy(false);
		setRemoving(null);
	};

	const companyLabel = (bill: FinancialBillDTO) => bill.monthly ? bill.companyName : `${bill.companyName} (avulsa ${formatDate(bill.issueDate)})`;

	return (
		<div className="financial-documents">
			<div className="financial-filters">
				<FormControl size="small" sx={{ minWidth: 170 }}>
					<InputLabel id="documents-month">Mês</InputLabel>
					<Select labelId="documents-month" label="Mês" value={month} onChange={(e) => setMonth(e.target.value)}>
						{months.map(item => <MenuItem key={item} value={item}>{monthLabel(item)}</MenuItem>)}
					</Select>
				</FormControl>
				<TextField size="small" label="Buscar por nome ou CNPJ" value={search} onChange={(e) => setSearch(e.target.value)}
					sx={{ width: { xs: '100%', md: '280px' } }}/>
				<FormControl size="small" sx={{ minWidth: 150 }}>
					<InputLabel id="documents-checked">Filtrar</InputLabel>
					<Select labelId="documents-checked" label="Filtrar" value={checkedFilter} onChange={(e) => setCheckedFilter(e.target.value)}>
						<MenuItem value="ALL">Todas</MenuItem>
						<MenuItem value="CHECKED">Conferidas</MenuItem>
					</Select>
				</FormControl>
			</div>
			{loading
				? <div className="financial-loading"><CircularProgress/></div>
				: <TableContainer className="financial-table">
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell>
									<TableSortLabel active direction={ascending ? 'asc' : 'desc'} onClick={() => setAscending(!ascending)}>
										Empresa
									</TableSortLabel>
								</TableCell>
								<TableCell>CNPJ</TableCell>
								<TableCell align="center">Conferida</TableCell>
								<TableCell>Documentos anexados</TableCell>
								<TableCell align="center">Envio</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{visible.map(row => (
								<TableRow key={row.bill.idConta} hover>
									<TableCell>{companyLabel(row.bill)}</TableCell>
									<TableCell className="financial-nowrap">{row.bill.document}</TableCell>
									<TableCell align="center">
										<Tooltip title={row.checked ? 'Conferida na seção Conferência' : 'Ainda não conferida'}>
											{row.checked ? <CheckCircle color="success"/> : <RadioButtonUnchecked color="disabled"/>}
										</Tooltip>
									</TableCell>
									<TableCell>
										<div className="financial-attachments">
											{row.documents.map(document => (
												<Chip key={document.id} size="small" icon={<AttachFile/>} label={document.originalName || document.fileName}
													clickable component="a" target="_blank" rel="noopener"
													href={`/api/documents/view/internal/${encodeURIComponent(document.fileName)}`}
													onDelete={row.delivery ? undefined : () => setRemoving(document)}/>
											))}
											<Button size="small" disabled={!!row.delivery} onClick={() => setUploadBill(row.bill)}>Adicionar arquivo</Button>
										</div>
									</TableCell>
									<TableCell align="center">
										{row.delivery
											? <div className="financial-delivery">
												<Typography variant="body2">{row.delivery.sent ? 'Envio registrado' : 'Envio pendente'}</Typography>
												<Link target="_blank" rel="noopener" underline="hover"
													href={`/envio-de-documentos/${row.delivery.groupId}/envios?empresa=${row.bill.companyId}`}>
													Acompanhar no Envio de documentos <OpenInNew fontSize="inherit"/>
												</Link>
											</div>
											: <Button size="small" variant="contained" startIcon={preparing === row.bill.idConta ? <CircularProgress size={14}/> : <Send/>}
												disabled={!row.documents.length || preparing !== null} onClick={() => prepareDelivery(row.bill)}>
												Enviar documentos
											</Button>
										}
									</TableCell>
								</TableRow>
							))}
							{!visible.length &&
								<TableRow>
									<TableCell colSpan={5} align="center">Nenhuma fatura encontrada.</TableCell>
								</TableRow>
							}
						</TableBody>
					</Table>
				</TableContainer>
			}
			<FinancialUploadModal bill={uploadBill} onClose={() => setUploadBill(null)} onUploaded={() => {
				setUploadBill(null);
				load();
			}}/>
			<Dialog open={!!removing} onClose={() => setRemoving(null)}>
				<DialogTitle>Remover arquivo</DialogTitle>
				<DialogContent>{`Remover "${removing?.originalName || removing?.fileName}" desta fatura?`}</DialogContent>
				<DialogActions>
					<Button onClick={() => setRemoving(null)}>Cancelar</Button>
					<Button variant="contained" color="error" disabled={removeBusy} onClick={removeDocument}
						startIcon={removeBusy ? <CircularProgress size={16}/> : undefined}>
						Remover
					</Button>
				</DialogActions>
			</Dialog>
			<CreateTemplateModal/>
			{context && deliveryBill &&
				<CreateDeliveryModal
					company={{ companyId: context.companyId, companyName: context.companyName, documents: [] }}
					dto={EMPTY_DELIVERY}
					edit={false}
					existingFiles={context.documents}
					onSubmit={submitDelivery}
				/>
			}
		</div>
	);
}

export default FinancialDocuments
